import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'GitHub',
  content: `
A GitHub connection lets agents read code and pull requests, read-only, in the repositories you choose. Agents can't push, comment or open anything.

There are two ways to connect: the **Pliable GitHub app** (no token to create or renew) or a **token** you create.

---

## With the Pliable GitHub app

1. Open the **Agents** page and find **Connections**. Choose **GitHub**, give the connection a name (for example \`ops-github\`), and keep **Pliable GitHub app** selected.
2. If the app isn't installed yet, click **Install the GitHub app**, choose your organization and the repositories agents may read, and come back. (It's the same app as **Settings → Git**; if that's set up, it's already installed.)
3. Pliable lists the repositories the app can read. Click **Add connection**.

Each run gets its own token, valid for an hour, that can only **read** the repositories its step lists, even though the app itself can do more. So a step must list them:

\`\`\`json
{ "connection": "ops-github", "capability": "code.read", "params": { "repos": ["ws-data-hub"] } }
\`\`\`

To let agents read another repository, add it to the app's repositories in GitHub (**Settings → GitHub Apps → Pliable → Configure**), then list it on the step.

---

## With a token

### 1. Create a fine-grained token

1. In GitHub, open **Settings → Developer settings → Personal access tokens → Fine-grained tokens** and click **Generate new token**.
2. **Resource owner:** your organization. **Repository access:** **Only select repositories**, and pick the ones agents may read.
3. **Repository permissions:** **Contents: Read-only** and **Pull requests: Read-only** (GitHub adds **Metadata: Read-only**). Nothing else.
4. Click **Generate token** and copy it. If your organization approves tokens, ask an owner to approve it.

The token's repositories are the only limit on what agents read, so choose them carefully.

### 2. Add the connection in Pliable

1. Open the **Agents** page and find **Connections**.
2. Choose **GitHub**, give the connection a name (for example \`ops-github\`), choose **Token**, and paste it.
3. Click **Add connection**.

---

## Use it in an agent

\`\`\`json
"capabilities": [{ "connection": "ops-github", "capability": "code.read", "params": {} }]
\`\`\`

By default it reads repositories and pull requests. To read more, list the toolsets, from \`repos\`, \`git\`, \`pull_requests\`, \`issues\`, \`actions\` and \`code_security\`, and give the token the matching read permissions. Through the Pliable GitHub app, only \`repos\` and \`pull_requests\` work for now:

\`\`\`json
{ "connection": "ops-github", "capability": "code.read", "params": { "toolsets": ["repos", "pull_requests", "actions"] } }
\`\`\`

---

## Troubleshooting

| What you see | What to do |
|---|---|
| "Not Found" for a repository you know exists | The token (or the app's installation) doesn't include that repository, or your organization hasn't approved the token yet. |
| A run fails with "list the repositories its code.read grant may read" | The step uses the Pliable GitHub app but lists no repositories. Add them under the grant's **Repositories**. |
| A run fails with "GitHub wouldn't issue a token" | A listed repository isn't one the app can read. Add it to the app's repositories in GitHub. |
| A token that worked stops working | Fine-grained tokens expire. Generate a new one and use **Replace secret**. |
`,
};

export default doc;
